import { getStore } from 'app/hermes-redux';
import { performTenancyLookup, lookupKeys } from '../globals/regional-api';
import * as CompanyActions from 'state/company/actions';
import * as GlobalActions from 'state/global/actions';
import { mapUserFields, isEmpty } from 'app/state/company/helpers';
import { getCompanySettings as reduxGetCompanySettings } from 'state/company-settings/actions';
import fetch from 'globals/wa-fetch';
import PageTitle from 'head/state/actions';
import { getSubdomainFromHost } from 'router/navigation';
import Language from 'language';
import { AppLogger, LogTypes } from '@workivate/tho-web-shared';
import * as Storage from 'globals/storage';
// TODO: for now the company store is linked to redux state, should be removed soon

const COMPANY_CACHE_TTL_MILLIS = 1000 * 60 * 60 * 24;

type CompanyCacheEntry = {
  company_id: string;
  body: any;
  expiresAt: number;
};

const getCachedCompany = (companyID): any | null => {
  const cachedCompany = Storage.get<CompanyCacheEntry>(
    Storage.storageKeys.companyCache,
    true,
  );

  if (!cachedCompany) {
    return null;
  }

  if (cachedCompany.expiresAt <= Date.now()) {
    try {
      Storage.remove(Storage.storageKeys.companyCache);
    } catch {
      // Ignore storage cleanup failures; the cache should never block company loading.
    }

    return null;
  }

  if (String(cachedCompany.company_id) !== String(companyID)) {
    return null;
  }

  return cachedCompany.body;
};

const setCachedCompany = (companyID, company) => {
  try {
    Storage.set<CompanyCacheEntry>(
      Storage.storageKeys.companyCache,
      {
        company_id: String(companyID),
        body: company,
        expiresAt: Date.now() + COMPANY_CACHE_TTL_MILLIS,
      },
      true,
    );
  } catch {
    // Ignore storage failures; the network response remains the source of truth.
  }
};

export function setCompany(comp, initialiseCompany?) {
  const dispatch = getStore().dispatch;
  if (comp['nickname']) {
    dispatch(PageTitle.setCompanyName(comp['nickname']));
  }
  dispatch(CompanyActions.setCompany(comp));

  if (initialiseCompany) {
    dispatch(GlobalActions.initialiseCompany(true));
  }
}

export function getCompany() {
  return getStore().getState().company || {};
}

export function getProfileVisibility() {
  const visibility = getCompany()['user_profile_visibility'] || {};

  return mapUserFields(visibility);
}

export function getProfileEditability() {
  const editability = getCompany()['user_profile_editability'] || {};

  return mapUserFields(editability);
}

export function getCompanyCreatedOn() {
  const currentCompany = getCompany();

  return currentCompany['created_on'];
}

export function isCompanyEmpty() {
  return isEmpty(getCompany());
}

export function isGroupingFeature(feature) {
  return getStore().getState().companySettings.grouping[feature];
}

export function getCompanySettings() {
  return getStore().dispatch(reduxGetCompanySettings(getCompany()));
}

export const setPreLoginCompanyDetails = async (newLocale?: string) => {
  const companyResponse = await fetchPreLoginCompanyDetails(
    getSubdomainFromHost(),
  );

  const { branding: company, sso } = companyResponse.body;

  if (company?.status === 200) {
    if (sso?.status === 200) {
      company.sso = sso;
    }

    setCompany(company);
  } else {
    // Set an empty object if the company wasn't returned from the lookup service
    setCompany({});
  }
  await Language.set({
    locale: newLocale,
    resetGlobalContent: false,
  });
};

const fetchPreLoginCompanyDetails = subdomain =>
  performTenancyLookup(lookupKeys.domain, subdomain, Language.getLocale());

export const fetchCompany = (companyID, forceRefresh = false) => {
  if (!companyID) {
    const error = 'no company id passed';

    AppLogger.logWithOptions({
      message: 'No company ID passed to fetchCompany',
      level: LogTypes.Error,
      error: error,
      shouldLogToDatadog: true,
      shouldLogToDynatrace: true,
    });

    return Promise.reject(error);
  }

  const {
    company,
    global: { isCompanyInitialised },
  } = getStore().getState();

  if (
    !forceRefresh &&
    isCompanyInitialised &&
    String(company?.company_id) === String(companyID)
  ) {
    return Promise.resolve({ body: company });
  }

  if (!forceRefresh) {
    const cachedCompany = getCachedCompany(companyID);

    if (cachedCompany) {
      setCompany(cachedCompany, true);
      return Promise.resolve({ body: cachedCompany });
    }
  }

  return fetch(`/companies/${companyID}`, {
    token: true,
    version: 1.3,
  }).then(response => {
    setCachedCompany(companyID, response.body);

    return response;
  });
};

// data: could be 'nickname', 'image_logo', 'image_cover', 'image_square_logo'
export function getBrandedData(keyName) {
  const { company } = getStore().getState();
  const asset = company[keyName];
  const hasAsset = !!company && !!asset;

  // only if nickname we want to return a string, because nickname is just a string
  if (keyName === 'nickname' && !hasAsset) return '';

  if (!hasAsset) return {};

  // if object we have an image and the server is returning an object for images
  // if nickname just return the string as it is
  // as ''.url === undefined => true this condition will return the string if no .url property in the string.
  return asset;
}
